import { IValidateLocationRestrictionsForTimePeriodsQuery } from '~/client/graph/operations/generated/LocationType.generated'
import { spaceLabelPrefix } from '~/client/src/shared/utils/util'

import type {
  IConflictingBooking,
  IOverlapConflict,
} from '../../../../../hoursOfOperationWarning'

type ITimePeriodResult = NonNullable<
  NonNullable<
    IValidateLocationRestrictionsForTimePeriodsQuery['validateLocationRestrictionsForTimePeriods']
  >[number]
>

export interface IBlockedOverlap {
  locationId: string
  violatedRestrictions: string[]
  bookings: IConflictingBooking[]
}

// Single source of truth for overlap blocking semantics. When a location is
// blocked without an explicit message, fall back to a generic reason so the
// boolean signal is never silently dropped downstream.
export const collectBlockedOverlaps = (
  locationResults: ITimePeriodResult['locationResults'] | undefined,
): IBlockedOverlap[] => {
  const blocked: IBlockedOverlap[] = []
  for (const v of locationResults || []) {
    const messages = (v?.violatedRestrictions || []).filter(
      (message): message is string => !!message,
    )
    const isBlocked = !v?.isOverlappingAllowed || messages.length > 0
    if (isBlocked && v?.locationId) {
      blocked.push({
        locationId: v.locationId,
        violatedRestrictions: messages.length
          ? messages.map(spaceLabelPrefix)
          : ['Overlapping booking limit reached'],
        bookings: (v.conflictingPermits || [])
          .filter(Boolean)
          .map(({ code, startDate, endDate }) => ({
            code,
            startDate,
            endDate,
          })),
      })
    }
  }
  return blocked
}

// One conflict per location across every occurrence: the limit and bookings
// come from the first occurrence that is full.
export const collectOverlapConflicts = (
  intervals:
    | Array<Pick<ITimePeriodResult, 'timePeriod' | 'locationResults'> | null>
    | null
    | undefined,
): Map<string, IOverlapConflict> => {
  const conflicts = new Map<string, IOverlapConflict>()
  const totalCount = intervals?.length ?? 0
  for (const interval of intervals || []) {
    for (const {
      locationId,
      violatedRestrictions,
      bookings,
    } of collectBlockedOverlaps(interval?.locationResults)) {
      const existing = conflicts.get(locationId)
      if (existing) {
        existing.failingCount += 1
        continue
      }
      const [, limit] = violatedRestrictions[0].match(/\d+\s*\/\s*(\d+)/) ?? []
      conflicts.set(locationId, {
        limit: limit ? Number(limit) : null,
        bookings,
        failingCount: 1,
        totalCount,
        firstFailingStart: interval?.timePeriod?.startDate,
      })
    }
  }
  return conflicts
}
